import { useAuth } from "@/context/auth";
import { trackEvent } from "@/services/analytics-service";
import { AnalyticsKey } from "@/types/analytics";
import { useTheme } from "@/context/theme";
import {
  MenuItem,
  MenuList,
  MenuButton,
  MenuPopover,
  MenuTrigger,
  Menu,
  Avatar,
  Caption1Stronger,
  Caption2Strong,
  teamsDarkTheme,
  Button,
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Link,
} from "@fluentui/react-components";
import {
  ArrowExitFilled,
  ArrowLeft12Filled,
  Settings24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
} from "@fluentui/react-icons";
import React, { useState } from "react";
import { usePrivateRouteHeaderStyles } from "./styles";
import { useDocumentClassification } from "@/context/document-classification";
import { useRoute } from "@/context/router";
import { TabName, TABS_DATA } from "@/routes";
import log from "../../../utils/logger";

export const PrivateRouteHeader = () => {
  const { user, logout, logoutWithOkta } = useAuth();
  const { toggleTheme, theme } = useTheme();
  const styles = usePrivateRouteHeaderStyles();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { handleClearData } = useDocumentClassification();
  const { currentRoute, setCurrentRoute } = useRoute();
  const [activeTab, setActiveTab] = useState<TabName>("risk-analysis");

  const handleLogout = async () => {
    if (user) {
      trackEvent(AnalyticsKey.AUTH.LOGOUT, {
        userId: user.user.id,
        username: user.user.name,
      });
    }

    handleClearData();
    try {
      await Promise.all([logout(), logoutWithOkta()]);
    } catch (error) {
      log.error("Error logging out", undefined, error);
    }
  };

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    const routeName = data.value as TabName;
    setCurrentRoute(routeName);
    setActiveTab(routeName);
  };

  const isDarkTheme = theme === teamsDarkTheme;

  return (
    <div style={{ maxWidth: "100%", overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {currentRoute === "settings" && (
          <Button
            icon={<ArrowLeft12Filled />}
            onClick={() => setCurrentRoute("risk-analysis")}
            appearance="transparent"
            className={styles.backButton}
          >
            Back
          </Button>
        )}

        <Menu open={isMenuOpen} onOpenChange={(_, { open }) => setIsMenuOpen(open)}>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton appearance="transparent" className={styles.menuButton}>
              <Avatar
                active={isMenuOpen ? "active" : "inactive"}
                activeAppearance="ring"
                name={user?.user.name}
                size={32}
                color="brand"
              />
              <div className={styles.userInfo}>
                <Caption1Stronger>{user?.user?.name}</Caption1Stronger>
                {user?.user?.username && user?.user?.username !== user?.user?.name && (
                  <Caption2Strong>{user?.user?.username}</Caption2Strong>
                )}
              </div>
            </MenuButton>
          </MenuTrigger>

          <MenuPopover>
            <MenuList>
              <MenuItem icon={isDarkTheme ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />} onClick={toggleTheme}>
                {isDarkTheme ? "Light Theme" : "Dark Theme"}
              </MenuItem>
              <MenuItem icon={<ArrowExitFilled />} onClick={handleLogout}>
                Logout
              </MenuItem>
              <MenuItem icon={<Settings24Regular />} onClick={() => setCurrentRoute("settings")}>
                Settings
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
      {!user.user.key && (
        <MessageBar intent="error" style={{ margin: "0.5rem 0" }}>
          <MessageBarBody>
            <MessageBarTitle>WARNING!</MessageBarTitle>
            Your account doesn&apos;t have an OpenAI key setup. Go to{" "}
            <Link href="#" onClick={() => setCurrentRoute("settings")}>
              settings
            </Link>{" "}
            to provide one.
          </MessageBarBody>
        </MessageBar>
      )}
      {currentRoute !== "settings" && (
        <TabList selectedValue={activeTab} onTabSelect={onTabSelect} className={styles.tabList}>
          {Object.entries(TABS_DATA).map(([key, tab]) => (
            <Tab key={key} id={key} value={key} icon={<tab.Icon />} className={styles.tab}>
              {tab.title}
            </Tab>
          ))}
        </TabList>
      )}
    </div>
  );
};
